import * as THREE from 'three';
import {DEFAULT_CARCASE_THICKNESS} from 'components/customer/RoomPlanner/constants/cabinetDefaults';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    AdvancedShelf,
    PreviewFormValues,
    AdvancedUpperShelf,
    AdvancedLowerShelf,
    AdvancedMiddleShelf,
} from 'components/customer/Preview3D/types';
import {addDragEffectOnClick} from 'components/customer/Preview3D/helpers';
import {
    loadCachedTexture,
    clearTextureCache,
    resolveTexture,
} from 'components/customer/Preview3D/helpers/Three';

export {clearTextureCache};

import {
    DEFAULT_VENT_FRONT_WIDTH,
    DEFAULT_VENT_WIDTH,
} from 'components/customer/Preview3D/values/useRangehoodDynamicValues';
import {rotateAroundCenter} from 'components/customer/RoomPlanner/helpers/rotateAroundCenter';
import {CabinetTopEnum} from 'shared/CabinetTop';
import {applyQuarterCircleArcShape} from 'components/customer/Preview3DCommon/shapes/shapeUtils';
import {
    applyCarcaseMaterial,
    applyWallFlushOutlineFix,
    attachWireframeToggleEvent,
    createLineSegments,
    buildLShapedReturnShelves,
    buildClipPanel,
    buildCornerShapedPanel,
    buildUShapedPanelShelves,
    buildRadiusPanel,
    buildShelvesWithOvenVents,
    buildShelvesWithRangehoodCutout,
    buildCornerShapePanelForPantry,
    buildCornerShapePanelFor3PartPantry,
    buildLShapedPanelForPantry,
    buildLShapedPanelForPantryReturn,
    buildUpperAndLowerLeftRightUShapeShelves,
    buildUpperAndLowerUShapeShelves,
    buildBottomPanelWithVent,
    buildTopBottomLShapedPanels,
    buildLShapedPanelShelves,
    buildSingleLShapePanel,
    resolveShapedShelfCount,
} from 'components/customer/Preview3DCommon/shelves/common';

export const getCarcaseThickness = (values: PreviewFormValues): number =>
    Number(values.cabinet_carc_thickness) || DEFAULT_CARCASE_THICKNESS;

export const getShelfThickness = (values: PreviewFormValues): number =>
    Number(values.simple_shelves_thickness) ||
    Number(values.cabinet_carc_thickness) ||
    DEFAULT_CARCASE_THICKNESS;

/**
 * @param {PreviewFormValues} values Cabinet form values.
 * @param {boolean} isShelf Whether the panel is a shelf rather than carcase.
 * @param {number} carcaseThickness Thickness of the carcase material.
 * @return {{panelThickness: number, centerOffset: number}} Extrusion thickness
 * and the centring shift along the panel's own thickness axis.
 */
export const getPanelThickness = (
    values: PreviewFormValues,
    isShelf: boolean,
    carcaseThickness: number
) => {
    const panelThickness = isShelf
        ? getShelfThickness(values)
        : carcaseThickness;

    return {
        panelThickness,
        centerOffset: (carcaseThickness - panelThickness) / 2,
    };
};

// Same URL rule as RoomPlanner applyImagePath, so preloads hit the cache
// entries the panels load from (images may already carry the /uploads path).
const toMaterialImageUrl = (image: string | undefined) =>
    image?.startsWith('/uploads')
        ? image
        : `/uploads/gocabinet_materials/${image}`;

export const getCarcaseTextures = (values: PreviewFormValues) => {
    const carcaseUrl = values.cabinet_carc_image
        ? values.cabinet_carc_image
        : toMaterialImageUrl(values.carc_material?.image);
    const carcaseEdgeUrl = values.cabinet_carc_edge_image
        ? values.cabinet_carc_edge_image
        : toMaterialImageUrl(values.carc_edge?.image);

    void loadCachedTexture(carcaseUrl);
    void loadCachedTexture(carcaseEdgeUrl);

    return {
        carcaseTexture: resolveTexture(carcaseUrl),
        carcaseEdgeTexture: resolveTexture(carcaseEdgeUrl),
    };
};

export const getExteriorTextures = (values: PreviewFormValues) => {
    const exteriorUrl = values.cabinet_ext_image
        ? values.cabinet_ext_image
        : toMaterialImageUrl(values.ext_material?.image);
    const exteriorEdgeUrl = values.cabinet_ext_edge_image
        ? values.cabinet_ext_edge_image
        : toMaterialImageUrl(values.ext_edge?.image);

    void loadCachedTexture(exteriorUrl);
    void loadCachedTexture(exteriorEdgeUrl);

    return {
        exteriorTexture: resolveTexture(exteriorUrl),
        exteriorEdgeTexture: resolveTexture(exteriorEdgeUrl),
    };
};

/**
 * Kicks off texture loading for a cabinet's carcase and exterior materials
 * without blocking the caller. Call this for every product before the
 * sequential render loop so that all texture network requests run in parallel.
 * By the time the loop reaches each cabinet, its textures will already be
 * cached or in-flight — eliminating the per-cabinet texture download stall.
 * @param {PreviewFormValues} values Cabinet form values.
 */
export const preloadCabinetTextures = (values: PreviewFormValues): void => {
    getCarcaseTextures(values);
    getExteriorTextures(values);
};

export const generateLShapeMesh = ({
    leftWidth,
    leftDepth,
    rightDepth,
    radius,
    rightWidth,
    carcaseTexture,
    carcaseEdgeTexture,
    carcaseThickness,
    horizontalGrainCarc,
    showTexture,
}: {
    leftWidth: number;
    leftDepth: number;
    rightDepth: number;
    rightWidth: number;
    radius: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    carcaseThickness: number;
    horizontalGrainCarc?: boolean;
    showTexture?: boolean;
}) => {
    // Create the shape
    const shape = new THREE.Shape();

    // Clamp radius to fit within both horizontal and vertical constraints
    const maxRadiusHorizontal = rightWidth - leftDepth;
    const maxRadiusVertical = leftWidth - rightDepth;
    const clampedRadius = Math.min(
        radius,
        maxRadiusHorizontal,
        maxRadiusVertical
    );

    // Start at the bottom-left corner
    shape.moveTo(0, 0);

    // Draw the left vertical line
    shape.lineTo(0, leftWidth);

    // Draw the top horizontal line to the right (end of left width)
    shape.lineTo(leftDepth, leftWidth);

    // Draw the vertical line downwards to the radius start point
    shape.lineTo(leftDepth, rightDepth + clampedRadius);

    // Create the rounded corner with absarc
    shape.absarc(
        leftDepth + clampedRadius, // x coordinate of arc's center
        rightDepth + clampedRadius, // y coordinate of arc's center
        clampedRadius, // radius of the arc
        Math.PI, // start angle (180 degrees in radians)
        Math.PI * 1.5, // end angle (270 degrees in radians)
        false // clockwise direction
    );

    // Draw the remaining horizontal line to the right
    shape.lineTo(rightWidth, rightDepth);

    // Draw the right vertical line upwards
    shape.lineTo(rightWidth, 0);

    // Close the shape by drawing the bottom horizontal line to the left
    shape.lineTo(0, 0);

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: false,
        hasHorizontalGrain: Boolean(horizontalGrainCarc),
        showTexture,
    });

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};

export const renderSingleLShapePanel = (values: PreviewFormValues) => {
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    const carcaseThickness = getCarcaseThickness(values);
    let lineMaterialForDrag: THREE.LineBasicMaterial | null = null;

    const mesh = buildSingleLShapePanel({
        values,
        carcaseThickness,
        makeLShapeMesh: ({
            leftWidth,
            leftDepth,
            rightDepth,
            radius,
            rightWidth,
        }) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness,
            }),
        lineSegmentsForMesh: (mesh) => {
            // L-shape panels sit flush against the wall in the RoomPlanner.
            // Push the panel face slightly back in the depth buffer
            // (polygonOffset) so its outline beats the coplanar wall without
            // disabling depth testing on the line — that avoids the x-ray
            // look.
            applyWallFlushOutlineFix(mesh);
            const lineSegments = createLineSegments(mesh);
            lineMaterialForDrag = lineSegments.material;
            return lineSegments;
        },
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
        postProcess: (mesh) => rotateAroundCenter(mesh, Math.PI),
    });

    // attach userdata drag state events (needs line material)
    if (lineMaterialForDrag) {
        addDragEffectOnClick(
            mesh as unknown as THREE.Mesh<
                THREE.BoxGeometry,
                THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[]
            >,
            lineMaterialForDrag
        );
    }

    return mesh;
};

export const renderLShapedPanel = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const isLsSolidTop =
        values.cabinet_top == CabinetTopEnum.LS_SOLID_TOP ||
        !!variables?.isLsSolidTop;

    const height = Number(values.cabinet_height);
    const isUpperProduct = Boolean(variables?.isUpperProduct);
    const radius = 20;
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);

    const rightWidth =
        Number(values.cabinet_right_width) -
        carcaseThickness -
        carcaseThickness;
    const leftWidth =
        Number(values.cabinet_left_width) +
        2 +
        (isUpperProduct
            ? -(carcaseThickness * 2) - 2
            : -(carcaseThickness * 2));

    // Pantry and return L-shapes draw their shelves from their own renderers, so
    // keep the historical shelves-array count for them. Every other L-shape takes
    // its count from simple_shelves_amount, matching the mini preview.
    const usesDedicatedShelfRenderer =
        Boolean(variables?.isLShapedPantry) ||
        Boolean(variables?.isReturnProduct);
    const shelvesCount = usesDedicatedShelfRenderer
        ? Number(values.shelves?.length) || 0
        : resolveShapedShelfCount(values);
    const advancedShelves = values.shelves as unknown as AdvancedShelf[];
    const isUpper3DoorProduct =
        variables?.is3DoorProduct && variables?.isUpperProduct;
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    return buildLShapedPanelShelves({
        values,
        variables,
        shelvesCount,
        shelves: advancedShelves,
        carcaseThickness,
        shelfThickness,
        height,
        leftWidth,
        rightWidth,
        radius,
        isLsSolidTop,
        isOpenLShape: Boolean(variables?.isOpenLShape),
        isUpper3DoorProduct: Boolean(isUpper3DoorProduct),
        makeLShapeMesh: ({leftDepth, rightDepth, thickness}) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: thickness,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const generateShelfWithOvenVentMesh = ({
    width,
    depth,
    carcaseTexture,
    carcaseEdgeTexture,
    carcaseThickness,
    values,
}: {
    width: number;
    depth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    carcaseThickness: number;
    values: PreviewFormValues;
}) => {
    const shape = new THREE.Shape();

    // Panel dimensions

    // Hole dimensions
    const holeWidth = (width - 300) / 2;
    const holeHeight = depth - 80;
    const gap = 100;

    // Hole positions
    const hole1X = 100;
    const hole2X = hole1X + holeWidth + gap;
    const holeY = 45;

    shape.moveTo(0, 0);
    shape.lineTo(width, 0);
    shape.lineTo(width, depth);
    shape.lineTo(0, depth);
    shape.lineTo(0, 0);

    // Create the first hole (a rectangle with dynamic position and size)
    const hole1 = new THREE.Path();
    hole1.moveTo(hole1X, holeY);
    hole1.lineTo(hole1X + holeWidth, holeY);
    hole1.lineTo(hole1X + holeWidth, holeY + holeHeight);
    hole1.lineTo(hole1X, holeY + holeHeight);
    hole1.lineTo(hole1X, holeY);
    shape.holes.push(hole1);

    // Create the second hole (a rectangle with dynamic position and size)
    const hole2 = new THREE.Path();
    hole2.moveTo(hole2X, holeY);
    hole2.lineTo(hole2X + holeWidth, holeY);
    hole2.lineTo(hole2X + holeWidth, holeY + holeHeight);
    hole2.lineTo(hole2X, holeY + holeHeight);
    hole2.lineTo(hole2X, holeY);
    shape.holes.push(hole2);

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: true,
        flipGrain: true,
        hasHorizontalGrain: Boolean(values.hor_grain_carc),
    });

    const position = geometry.attributes.position;
    const faceCount = position.count / 3;

    for (let i = 0; i < faceCount; i++) {
        const vertex1 = new THREE.Vector3().fromBufferAttribute(
            position,
            i * 3
        );
        const vertex2 = new THREE.Vector3().fromBufferAttribute(
            position,
            i * 3 + 1
        );
        const vertex3 = new THREE.Vector3().fromBufferAttribute(
            position,
            i * 3 + 2
        );

        if (vertex1.z === 0 && vertex2.z === 0 && vertex3.z === 0) {
            geometry.addGroup(i * 3, 3, 0); // Front face
        } else if (
            vertex1.z === extrudeSettings.depth &&
            vertex2.z === extrudeSettings.depth &&
            vertex3.z === extrudeSettings.depth
        ) {
            geometry.addGroup(i * 3, 3, 1); // Back face
        } else {
            geometry.addGroup(
                i * 3,
                3,
                [33, 32].includes(geometry.groups?.length) ? 2 : 1
            ); // Sides
        }
    }

    return new THREE.Mesh(geometry, materials);
};

export const renderShelvesWithOvenVents = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    return buildShelvesWithOvenVents({
        values,
        variables,
        carcaseThickness,
        shelfThickness,
        makeVentShelfMesh: ({width, depth, thickness}) =>
            generateShelfWithOvenVentMesh({
                width,
                depth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: thickness,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

const getLShapeOffsetsForReturn = (carcaseThickness: number) => {
    let widthOffset = 0;
    let positionXOffset = 0;
    let positionXOffsetRightReturn = 0;

    switch (Number(carcaseThickness)) {
        case 16.5:
            widthOffset = carcaseThickness * 4;
            positionXOffset = carcaseThickness + 5;
            positionXOffsetRightReturn =
                carcaseThickness + carcaseThickness / 2;
            break;
        case 25.5:
            widthOffset = carcaseThickness * 3;
            positionXOffset = carcaseThickness;
            positionXOffsetRightReturn =
                carcaseThickness + carcaseThickness / 2;
            break;
        case 12.5:
        case 12:
            widthOffset = carcaseThickness * 5;
            positionXOffset = carcaseThickness * 2;
            positionXOffsetRightReturn =
                carcaseThickness + carcaseThickness / 2;
            break;
        default:
            widthOffset = carcaseThickness * 2.5;
            positionXOffset = carcaseThickness * 0.5;
            positionXOffsetRightReturn = carcaseThickness;
            break;
    }

    return [widthOffset, positionXOffset, positionXOffsetRightReturn];
};

export const renderLShapedPanelForReturn = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const advancedShelves = values.shelves as unknown as AdvancedShelf[];
    const shelvesCount = advancedShelves?.length;
    const isLeftReturn = variables?.isLeftReturn;

    const includeFaces =
        typeof values.include_drawer_faces !== 'undefined'
            ? Boolean(values.include_drawer_faces)
            : true;
    const voidWidth =
        values.cabinet_void_width &&
        Boolean(values.cabinet_cover_void) &&
        includeFaces
            ? Number(values.cabinet_void_width)
            : 0;
    const returnPanelWidth = values.cabinet_return_panel_width
        ? parseFloat(values.cabinet_return_panel_width?.toString())
        : 0;

    const [widthOffset] = getLShapeOffsetsForReturn(carcaseThickness);

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    return buildLShapedReturnShelves({
        values,
        variables,
        shelves: advancedShelves,
        shelvesCount,
        carcaseThickness,
        shelfThickness,
        widthOffset,
        voidWidth,
        returnPanelWidth,
        isLeftReturn,
        makeLShapeMesh: ({
            leftWidth,
            leftDepth,
            rightDepth,
            radius,
            rightWidth,
        }) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: shelfThickness,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

const generateUShapeMesh = ({
    width,
    depth,
    cutoutWidth,
    cutoutHeight,
    radius,
    carcaseTexture,
    carcaseEdgeTexture,
    complexRadii,
    sides,
    customCutoutHeight,
    carcaseThickness,
    values,
}: {
    width: number;
    depth: number;
    cutoutWidth: number;
    cutoutHeight: number;
    radius: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    complexRadii?: {
        interiorRadius: number;
        exteriorRadius: number;
    };
    sides?: {
        left: number;
        right: number;
    };
    customCutoutHeight?: number;
    carcaseThickness: number;
    values: PreviewFormValues;
}) => {
    const shape = new THREE.Shape();
    const exteriorRadius = complexRadii ? complexRadii.exteriorRadius : radius;
    const interiorRadius = complexRadii ? complexRadii.interiorRadius : radius;

    const leftLength = sides ? sides.left : (width - cutoutWidth) / 2;
    const rightLength = sides ? width - sides.right : (width + cutoutWidth) / 2;

    const computedCutoutHeight = customCutoutHeight
        ? customCutoutHeight
        : cutoutHeight;

    // Outer shape with rounded corners and open bottom, starting from (0,0)
    shape.moveTo(0, 0);
    shape.absarc(width, 0, 0, Math.PI * 1.5, 0, false);
    shape.lineTo(width, depth);
    shape.lineTo(0, depth);
    shape.lineTo(0, 0);

    // Draw the cutout using lines and arcs
    shape.lineTo(leftLength - exteriorRadius, 0);
    shape.absarc(
        leftLength - exteriorRadius,
        exteriorRadius,
        exteriorRadius,
        Math.PI * 1.5,
        0,
        false
    );
    shape.lineTo(leftLength, computedCutoutHeight - interiorRadius);
    shape.absarc(
        leftLength + interiorRadius,
        computedCutoutHeight - interiorRadius,
        interiorRadius,
        Math.PI,
        Math.PI * 0.5,
        true
    );
    shape.lineTo(rightLength - interiorRadius, computedCutoutHeight);
    shape.absarc(
        rightLength - interiorRadius,
        computedCutoutHeight - interiorRadius,
        interiorRadius,
        Math.PI * 0.5,
        0,
        true
    );
    shape.lineTo(rightLength, computedCutoutHeight - interiorRadius);
    shape.lineTo(rightLength, exteriorRadius);
    shape.absarc(
        rightLength + exteriorRadius,
        exteriorRadius,
        exteriorRadius,
        Math.PI,
        Math.PI * 1.5,
        false
    );

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: true,
        hasHorizontalGrain: Boolean(values.hor_grain_carc),
    });

    return new THREE.Mesh(geometry, materials);
};

export const renderUShapedPanel = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const advancedShelves = values.shelves as unknown as AdvancedShelf[];
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    return buildUShapedPanelShelves({
        values,
        variables,
        shelves: advancedShelves,
        carcaseThickness,
        shelfThickness,
        makeUShapeMesh: ({width, depth, cutoutWidth, cutoutHeight, radius}) =>
            generateUShapeMesh({
                width,
                depth,
                cutoutWidth,
                cutoutHeight,
                radius,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: shelfThickness,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderUpperAndLowerLeftRightUShapedShelves = (
    values: PreviewFormValues
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerLeftShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];
    const lowerRightShelves = values.shelves as unknown as AdvancedShelf[];

    const partitionWidth = values.cabinet_partition_width
        ? values.cabinet_partition_width < 1
            ? parseFloat(values.cabinet_width?.toString()) *
              values.cabinet_partition_width
            : parseFloat(values.cabinet_partition_width?.toString())
        : 0;

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    return buildUpperAndLowerLeftRightUShapeShelves({
        values,
        carcaseThickness,
        shelfThickness,
        upperShelves,
        lowerLeftShelves,
        lowerRightShelves,
        partitionWidth,
        makeUShapeMesh: ({width, depth, cutoutWidth, cutoutHeight, radius}) =>
            generateUShapeMesh({
                width,
                depth,
                cutoutWidth,
                cutoutHeight,
                radius,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: shelfThickness,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

const generateFfRailOnFlatMesh = ({
    leftWidth,
    leftDepth,
    rightDepth,
    rightWidth,
    carcaseTexture,
    carcaseEdgeTexture,
    carcaseThickness,
    values,
}: {
    leftWidth: number;
    leftDepth: number;
    rightDepth: number;
    rightWidth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    carcaseThickness: number;
    values: PreviewFormValues;
}) => {
    // Create the shape
    const shape = new THREE.Shape();

    const railWidth = 120;

    shape.moveTo(leftDepth - railWidth, carcaseThickness);
    shape.lineTo(leftDepth, carcaseThickness);
    shape.lineTo(rightWidth, leftWidth - rightDepth);
    shape.lineTo(rightWidth, leftWidth - rightDepth + railWidth);
    shape.lineTo(leftDepth - railWidth, carcaseThickness);

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: false,
        hasHorizontalGrain: Boolean(values.hor_grain_carc),
    });

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};

const getCornerShapeOffsets = (carcaseThickness: number) => {
    const defaultWidthOffset = carcaseThickness * 3;
    let widthOffset = 0;

    switch (Number(carcaseThickness)) {
        case 12.5:
        case 12:
        case 25.5:
            widthOffset = carcaseThickness * 3 + 1;
            break;
        default:
            widthOffset = defaultWidthOffset;
            break;
    }

    return [widthOffset];
};

const getCornerLShapeOffsets = (carcaseThickness: number) => {
    const defaultWidthOffset = carcaseThickness * 3;
    let widthOffset = 0;

    switch (Number(carcaseThickness)) {
        case 16.5:
            widthOffset = defaultWidthOffset;
            break;
        case 25.5:
            widthOffset = carcaseThickness * 2;
            break;
        case 12.5:
        case 12:
            widthOffset = carcaseThickness * 4;
            break;
        default:
            widthOffset = carcaseThickness * 1.5;
            break;
    }

    return [widthOffset];
};

const generateCornerShapeMesh = ({
    leftWidth,
    leftDepth,
    rightDepth,
    rightWidth,
    carcaseTexture,
    carcaseEdgeTexture,
    thickness = 16.5,
    values,
    variables,
}: {
    leftWidth: number;
    leftDepth: number;
    rightDepth: number;
    rightWidth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    thickness?: number;
    values: PreviewFormValues;
    variables: TemplateVariable;
}) => {
    const horizontalGrainCarc = Boolean(values.hor_grain_carc);
    const horizontalGrainExt = Boolean(values.hor_grain_ext);

    // Create the shape
    const shape = new THREE.Shape();

    // Start at the bottom-left corner
    shape.moveTo(0, 0);

    // Draw the left vertical line
    shape.lineTo(0, leftWidth);

    // Draw the top horizontal line to the right (end of left width)
    shape.lineTo(leftDepth, leftWidth);

    // Draw the remaining horizontal line to the right
    shape.lineTo(rightWidth, rightDepth);

    // Draw the right vertical line upwards
    shape.lineTo(rightWidth, 0);

    // Close the shape by drawing the bottom horizontal line to the left
    shape.lineTo(0, 0);

    const extrudeSettings = {
        steps: 1,
        depth: thickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: variables?.isClipProduct ? true : false,
        hasHorizontalGrain: variables?.isClipProduct
            ? horizontalGrainExt
            : horizontalGrainCarc,
    });

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};

export const renderCornerShapedPanel = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const isFfRailOnFlat = values.cabinet_top == CabinetTopEnum.FF_RAIL_ON_FLAT;
    const isFfSolidTop = values.cabinet_top == CabinetTopEnum.FF_SOLID_TOP;

    const [widthOffset] = getCornerShapeOffsets(carcaseThickness);
    const rightWidth =
        values.cabinet_right_width - widthOffset + carcaseThickness * 2 + 2;
    const leftWidth = Number(values.cabinet_left_width) + 2;

    const shelvesCount = resolveShapedShelfCount(values);
    const advancedShelves = values.shelves as unknown as AdvancedShelf[];

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    return buildCornerShapedPanel({
        values,
        variables,
        shelvesCount,
        advancedShelves,
        carcaseThickness,
        shelfThickness,
        isFfRailOnFlat,
        isFfSolidTop,
        leftWidth,
        rightWidth,
        makeFfRailOnFlatMesh: () =>
            generateFfRailOnFlatMesh({
                leftWidth,
                leftDepth: values.cabinet_left_depth - carcaseThickness,
                rightDepth: Number(values.cabinet_right_depth),
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness,
                values,
            }),
        makeLShapeShelfMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius: 50,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: thickness,
            }),
        makeCornerShelfMesh: ({
            leftDepth,
            rightDepth,
            rightWidth,
            shouldApplyOffset,
            thickness,
        }) =>
            generateCornerShapeMesh({
                leftWidth,
                leftDepth: leftDepth - (shouldApplyOffset ? 6 : 1),
                rightDepth,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                thickness,
                values,
                variables,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

const generateRangehoodCutoutShapeMesh = ({
    width,
    depth,
    carcaseTexture,
    carcaseEdgeTexture,
    thickness = 16.5,
    carcaseThickness,
    values,
}: {
    width: number;
    depth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    thickness?: number;
    carcaseThickness: number;
    values: PreviewFormValues;
}) => {
    const ventRightPositionX =
        width / 2 + DEFAULT_VENT_FRONT_WIDTH / 2 - carcaseThickness / 2;
    const ventLeftPositionX =
        width / 2 - DEFAULT_VENT_FRONT_WIDTH / 2 - carcaseThickness / 2;
    const ventFrontPositionY = depth - DEFAULT_VENT_WIDTH - carcaseThickness;
    // Create the shape
    const shape = new THREE.Shape();

    shape.moveTo(0, 0);

    shape.lineTo(width, 0);
    shape.lineTo(width, depth);
    shape.lineTo(ventRightPositionX, depth);
    shape.lineTo(ventRightPositionX, ventFrontPositionY);
    shape.lineTo(ventLeftPositionX, ventFrontPositionY);
    shape.lineTo(ventLeftPositionX, depth);
    shape.lineTo(0, depth);

    // Close the shape by drawing the bottom horizontal line to the left
    shape.lineTo(0, 0);

    const extrudeSettings = {
        steps: 1,
        depth: thickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: true,
        hasHorizontalGrain: Boolean(values.hor_grain_carc),
    });

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};

export const renderShelvesWithRangehoodCutout = (values: PreviewFormValues) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const shelvesCount = values.cabinet_simple_shelves
        ? values.simple_shelves_amount
        : values.shelves?.length;

    const advancedShelves = values.shelves as unknown as AdvancedShelf[];
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    return buildShelvesWithRangehoodCutout({
        values,
        carcaseThickness,
        shelfThickness,
        shelvesCount,
        advancedShelves,
        makeRangehoodCutoutMesh: ({width, depth, thickness}) =>
            generateRangehoodCutoutShapeMesh({
                width,
                depth,
                carcaseTexture,
                carcaseEdgeTexture,
                thickness,
                carcaseThickness,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderClipPanel = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const exteriorThickness = values.cabinet_ext_thickness;
    const shelvesCount = values.cabinet_simple_shelves
        ? values.simple_shelves_amount
        : (values.shelves?.length ?? 0);

    const advancedShelves =
        (values.shelves as unknown as AdvancedShelf[]) ?? [];
    const {exteriorTexture, exteriorEdgeTexture} = getExteriorTextures(values);

    return buildClipPanel({
        values,
        variables,
        shelvesCount,
        advancedShelves,
        exteriorThickness,
        makeCornerShapeMesh: ({leftWidth, leftDepth, rightDepth, rightWidth}) =>
            generateCornerShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth,
                carcaseTexture: exteriorTexture,
                carcaseEdgeTexture: exteriorEdgeTexture,
                thickness: exteriorThickness,
                values,
                variables,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

const generateRadiusShapeMesh = ({
    width,
    depth,
    texture,
    edgeTexture,
    variables,
    horizontalGrainExt,
    exteriorThickness,
}: {
    width: number;
    depth: number;
    texture: THREE.Texture;
    edgeTexture: THREE.Texture;
    variables: TemplateVariable;
    horizontalGrainExt: boolean;
    exteriorThickness: number;
}) => {
    const shape = new THREE.Shape();

    applyQuarterCircleArcShape(shape, width, depth);

    const extrudeSettings = {
        steps: 1,
        depth: exteriorThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture,
        edgeTexture,
        useSubstrateEdge: false,
        flipGrain: !variables?.isLeftRadius,
        hasHorizontalGrain: horizontalGrainExt,
    });

    return new THREE.Mesh(geometry, materials);
};

export const renderRadiusPanel = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const exteriorThickness = values.cabinet_ext_thickness;
    const horizontalGrainExt = Boolean(values.hor_grain_ext);
    const shelvesCount = values.cabinet_simple_shelves
        ? values.simple_shelves_amount
        : (values.shelves?.length ?? 0);

    const advancedShelves =
        (values.shelves as unknown as AdvancedShelf[]) ?? [];

    const {exteriorTexture, exteriorEdgeTexture} = getExteriorTextures(values);

    return buildRadiusPanel({
        values,
        variables,
        shelvesCount,
        advancedShelves,
        exteriorThickness,
        makeRadiusMesh: ({width, depth, offset}) =>
            generateRadiusShapeMesh({
                width: (!variables?.isLeftRadius ? depth : width) - offset,
                depth: (!variables?.isLeftRadius ? width : depth) - offset,
                texture: exteriorTexture,
                edgeTexture: exteriorEdgeTexture,
                variables,
                horizontalGrainExt,
                exteriorThickness,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderCornerShapePanelFor3PartPantry = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const [widthOffset] = getCornerShapeOffsets(carcaseThickness);
    const [widthOffsetLShape] = getCornerLShapeOffsets(carcaseThickness);

    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const middleShelvesCount = values?.middle_shelves
        ? (values?.middle_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const middleShelves =
        values.middle_shelves as unknown as AdvancedMiddleShelf[];
    const lowerShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];

    const cabinetUpperPartitionHeight =
        Number(values.cabinet_upper_partition_height) > 1
            ? Number(values.cabinet_upper_partition_height) /
              Number(values.cabinet_height)
            : Number(values.cabinet_upper_partition_height);
    const cabinetLowerPartitionHeight =
        Number(values.cabinet_lower_partition_height) > 1
            ? Number(values.cabinet_lower_partition_height) /
              Number(values.cabinet_height)
            : Number(values.cabinet_lower_partition_height);

    const upperShelfOffset =
        cabinetUpperPartitionHeight * Number(values.cabinet_height);
    const lowerShelfOffset =
        cabinetLowerPartitionHeight * Number(values.cabinet_height);

    const heightUpper = Number(values.cabinet_height) - upperShelfOffset;
    const heightMiddle = upperShelfOffset - lowerShelfOffset;
    const heightLower = lowerShelfOffset;

    const rightWidth = values.cabinet_right_width - widthOffset;
    const leftWidth = Number(values.cabinet_left_width) + 2;
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    return buildCornerShapePanelFor3PartPantry({
        values,
        variables,
        carcaseThickness,
        shelfThickness,
        leftWidth,
        rightWidth,
        widthOffsetLShape,
        upperShelves,
        middleShelves,
        lowerShelves,
        upperShelvesCount,
        middleShelvesCount,
        lowerShelvesCount,
        upperShelfOffset,
        lowerShelfOffset,
        heightUpper,
        heightMiddle,
        heightLower,
        lowerEndExtra: variables?.noCornerBaseBottom ? 0 : 1,
        makeLShapeMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius: 50,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: thickness,
            }),
        makeCornerShapeMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
            generateCornerShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth: rightWidth + carcaseThickness * 2 + 2,
                carcaseTexture,
                carcaseEdgeTexture,
                thickness,
                values,
                variables,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

const createLShapePartition = ({
    leftWidth,
    leftDepth,
    rightDepth,
    radius,
    rightWidth,
    carcaseTexture,
    carcaseEdgeTexture,
    heightOffset,
    carcaseThickness,
    isLeftReturn = false,
    values,
}: {
    leftWidth: number;
    leftDepth: number;
    rightDepth: number;
    radius: number;
    rightWidth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    index: number;
    heightOffset: number;
    carcaseThickness: number;
    isLeftReturn: boolean;
    values: PreviewFormValues;
}) => {
    const partitionMesh = generateLShapeMesh({
        leftWidth,
        leftDepth,
        rightDepth,
        radius,
        rightWidth,
        carcaseTexture,
        carcaseEdgeTexture,
        carcaseThickness,
    });

    let positionX = carcaseThickness;

    // determines if the partition should face right or left
    if (!isLeftReturn) {
        // Mirror about the inner right wall to keep the partition inside the
        // carcase; rightWidth + widthOffset pushed it past the wall.
        positionX = Number(values.cabinet_width);
        partitionMesh.scale.x = -1;
    }

    partitionMesh.scale.y = -1;

    // assign position
    partitionMesh.position.set(positionX, leftWidth, heightOffset);

    return partitionMesh;
};

export const renderLShapedPanelForPantryReturn = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);

    const includeFaces =
        typeof values.include_drawer_faces !== 'undefined'
            ? Boolean(values.include_drawer_faces)
            : true;
    const voidWidth =
        values.cabinet_void_width &&
        Boolean(values.cabinet_cover_void) &&
        includeFaces
            ? Number(values.cabinet_void_width)
            : 0;
    const returnPanelWidth = values.cabinet_return_panel_width
        ? parseFloat(values.cabinet_return_panel_width?.toString())
        : 0;

    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];
    const shelvesHeightReference = {
        upper_shelves:
            parseFloat(values.cabinet_height?.toString()) -
            values.cabinet_partition_height,
        lower_shelves: values.cabinet_partition_height,
    };

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = Number(values.cabinet_height) - upperShelfOffset;

    const [widthOffset] = getLShapeOffsetsForReturn(carcaseThickness);

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    return buildLShapedPanelForPantryReturn({
        values,
        variables,
        carcaseThickness,
        shelfThickness,
        voidWidth,
        returnPanelWidth,
        upperShelves,
        lowerShelves,
        upperShelvesCount,
        lowerShelvesCount,
        upperShelfOffset,
        heightUpperSection: height,
        isLeftReturn: Boolean(variables?.isLeftReturn),
        widthOffset,
        makeShelfMesh: ({
            leftWidth,
            leftDepth,
            rightDepth,
            rightWidth,
            radius,
        }) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: shelfThickness,
            }),
        makePartitionMesh: ({
            leftWidth,
            leftDepth,
            rightDepth,
            rightWidth,
            radius,
            heightOffset,
        }) =>
            createLShapePartition({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                index: 0,
                heightOffset,
                isLeftReturn: Boolean(variables?.isLeftReturn),
                carcaseThickness,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderLShapedPanelForPantry = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);

    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];
    const shelvesHeightReference = {
        upper_shelves:
            parseFloat(values.cabinet_height?.toString()) -
            values.cabinet_partition_height,
        lower_shelves: values.cabinet_partition_height,
    };

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = Number(values.cabinet_height) - upperShelfOffset;

    const rightWidth = values.cabinet_right_width - carcaseThickness;
    const leftWidth = Number(values.cabinet_left_width) + 2;

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    return buildLShapedPanelForPantry({
        values,
        variables,
        carcaseThickness,
        shelfThickness,
        upperShelves,
        lowerShelves,
        upperShelvesCount,
        lowerShelvesCount,
        upperShelfOffset,
        height,
        leftWidth,
        rightWidth,
        radius: 20,
        makeLShapeMesh: ({leftDepth, rightDepth, thickness}) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius: 20,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: thickness,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderCornerShapePanelForPantry = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    const [widthOffset] = getCornerShapeOffsets(carcaseThickness);
    const [widthOffsetLShape] = getCornerLShapeOffsets(carcaseThickness);

    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];
    const shelvesHeightReference = {
        upper_shelves:
            Number(values.cabinet_height) -
            Number(values.cabinet_partition_height),
        lower_shelves: Number(values.cabinet_partition_height),
    };

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = Number(values.cabinet_height) - upperShelfOffset;

    const rightWidth = values.cabinet_right_width - widthOffset;
    const leftWidth = Number(values.cabinet_left_width) + 2;

    return buildCornerShapePanelForPantry({
        values,
        variables,
        carcaseThickness,
        shelfThickness,
        leftWidth,
        rightWidth,
        widthOffsetLShape,
        upperShelves,
        lowerShelves,
        upperShelvesCount,
        lowerShelvesCount,
        upperShelfOffset,
        height,
        lowerStartIndex: variables?.is2Part ? 0 : 1,
        lowerEndExtra: variables?.noCornerBaseBottom ? 0 : 1,
        makeLShapeMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius: 50,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: thickness,
            }),
        makeCornerShapeMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
            generateCornerShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth: rightWidth + carcaseThickness * 2 + 2,
                carcaseTexture,
                carcaseEdgeTexture,
                values,
                variables,
                thickness,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderUpperAndLowerUShapeShelves = (values: PreviewFormValues) => {
    const carcaseThickness = getCarcaseThickness(values);
    const shelfThickness = getShelfThickness(values);
    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];

    if (!upperShelvesCount && !lowerShelvesCount) return [];

    return buildUpperAndLowerUShapeShelves({
        values,
        upperShelves,
        lowerShelves,
        carcaseThickness,
        shelfThickness,
        makeUShapeMesh: ({width, depth, cutoutWidth, cutoutHeight, radius}) =>
            generateUShapeMesh({
                width,
                depth,
                cutoutWidth,
                cutoutHeight,
                radius,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness: shelfThickness,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const renderBottomPanelWithVent = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = getCarcaseThickness(values);
    const width = Number(values.cabinet_width) - carcaseThickness;
    const depth = Number(values.cabinet_depth) - carcaseThickness;
    const cutoutWidth = variables.cutoutWidth;
    const inset = variables.inset;

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);
    return buildBottomPanelWithVent({
        values,
        carcaseThickness,
        width,
        depth,
        cutoutWidth,
        inset,
        makeBottomPanelMesh: ({width, depth, cutoutWidth, inset}) =>
            generateBottomPanelWithVentMesh({
                width,
                depth,
                cutoutWidth,
                inset,
                carcaseTexture,
                carcaseEdgeTexture,
                values,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};

export const generateBottomPanelWithVentMesh = ({
    width,
    depth,
    cutoutWidth,
    inset,
    carcaseTexture,
    carcaseEdgeTexture,
    values,
}: {
    width: number;
    depth: number;
    cutoutWidth: number;
    inset: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    values: PreviewFormValues;
}) => {
    // Create the shape
    const shape = new THREE.Shape();

    shape.moveTo(0, 0);
    shape.lineTo(0, depth - inset); // left
    shape.lineTo(width, depth - inset); // front
    shape.lineTo(width, 0); // right
    shape.lineTo(width - cutoutWidth, 0); // back to right x offset
    shape.lineTo(width - cutoutWidth, cutoutWidth); // back to right y offset
    shape.lineTo(cutoutWidth, cutoutWidth); // back to left y offset
    shape.lineTo(cutoutWidth, 0); // back to left x offset
    shape.lineTo(0, 0); // close shape

    const carcaseThickness = getCarcaseThickness(values);
    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: false,
        hasHorizontalGrain: Boolean(values.hor_grain_carc),
    });

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};

export const renderTopBottomLShapedPanels = (
    values: PreviewFormValues,
    variables?: TemplateVariable
) => {
    const height = Number(values.cabinet_height);
    const carcaseThickness = getCarcaseThickness(values);
    const isUpperProduct = Boolean(variables?.isUpperProduct);
    const radius = 20;

    const rightWidth =
        Number(values.cabinet_right_width) -
        carcaseThickness -
        carcaseThickness;
    const leftWidth =
        Number(values.cabinet_left_width) +
        2 +
        (isUpperProduct
            ? -(carcaseThickness * 2) - 2
            : -(carcaseThickness * 2));

    const rightDepth =
        Number(values.cabinet_right_depth) -
        carcaseThickness -
        1 -
        carcaseThickness;
    const leftDepth =
        Number(values.cabinet_left_depth) - carcaseThickness - 1 + 1;

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    const isSolidTop = values.cabinet_top == CabinetTopEnum.SOLID_TOP;
    const panelCount = isSolidTop ? 2 : 1;
    return buildTopBottomLShapedPanels({
        values,
        variables,
        carcaseThickness,
        height,
        panelCount,
        leftWidth,
        rightWidth,
        leftDepth,
        rightDepth,
        radius,
        makeLShapeMesh: ({
            leftDepth,
            rightDepth,
            leftWidth,
            rightWidth,
            radius,
        }) =>
            generateLShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
                carcaseThickness,
                carcaseTexture,
                carcaseEdgeTexture,
            }),
        lineSegmentsForMesh: (mesh) => createLineSegments(mesh),
        attachEvents: (mesh) => attachWireframeToggleEvent(mesh),
    });
};
